<template>
  <div class="recommended-activity">
    <div class="activity-card">
      <div class="card-header">
        <div class="header-icon">
          <ViewIcon width="48" height="48" />
        </div>
        <h1 class="card-title">Recommended Activity</h1>
        <p class="card-subtitle">
          Take a closer look at one of our Makercart activities selected just
          for you.
        </p>
      </div>

      <div class="card-content">
        <template v-if="isLoading">
          <div class="skeleton-header">
            <div class="skeleton-title-section">
              <div class="skeleton-title-large"></div>
              <div class="skeleton-badge"></div>
            </div>
            <div class="skeleton-refresh-button"></div>
          </div>

          <div class="skeleton-description">
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line short"></div>
          </div>

          <div class="info-boxes">
            <div class="info-item-box skeleton-info-box">
              <div class="info-icon skeleton-icon"></div>
              <div class="info-content">
                <div class="skeleton-label"></div>
                <div class="skeleton-value"></div>
              </div>
            </div>
            <div class="info-item-box skeleton-info-box">
              <div class="info-icon skeleton-icon"></div>
              <div class="info-content">
                <div class="skeleton-label"></div>
                <div class="skeleton-value"></div>
              </div>
            </div>
            <div class="info-item-box skeleton-info-box">
              <div class="info-icon skeleton-icon"></div>
              <div class="info-content">
                <div class="skeleton-label"></div>
                <div class="skeleton-value"></div>
              </div>
            </div>
          </div>

          <div class="activity-details">
            <div class="activity-image-section">
              <div class="skeleton-image-large"></div>
            </div>
            <div class="outcomes-section">
              <div class="skeleton-outcomes-title"></div>
              <div class="skeleton-outcomes-intro"></div>
              <div class="skeleton-outcomes-list">
                <div class="skeleton-outcome-item"></div>
                <div class="skeleton-outcome-item"></div>
                <div class="skeleton-outcome-item short"></div>
              </div>
            </div>
          </div>
        </template>
        <div v-else-if="currentActivity" class="activity-container">
          <div class="activity-header-section">
            <div class="title-badge-container">
              <h2 class="activity-title">
                {{ currentActivity.makercart_activity }}
              </h2>
              <div class="subject-badge" v-if="currentActivity.subject">
                {{ currentActivity.subject }}
              </div>
            </div>
            <div class="refresh-button" @click="handleRefresh">
              <RefreshIcon width="16.667" height="16.667" color="#3b4648" />
              <span>Refresh</span>
            </div>
          </div>

          <p class="activity-description">
            {{
              currentActivity.activity_description ||
              "No description available."
            }}
          </p>

          <div class="info-boxes">
            <div class="info-item-box">
              <div class="info-icon">
                <svg
                  width="28.8"
                  height="28.8"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#E00069"
                    stroke-width="2"
                  />
                  <path
                    d="M12 6V12L16 14"
                    stroke="#E00069"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </div>
              <div class="info-content">
                <p class="info-label">Duration</p>
                <p class="info-value">
                  {{ currentActivity.duration || "N/A" }}
                </p>
              </div>
            </div>
            <div class="info-item-box">
              <div class="info-icon">
                <BookIcon width="28.8" height="28.8" color="#E00069" />
              </div>
              <div class="info-content">
                <p class="info-label">Materials</p>
                <p class="info-value">Included</p>
              </div>
            </div>
            <div class="info-item-box">
              <div class="info-icon">
                <PeopleIcon width="28.8" height="28.8" color="#E00069" />
              </div>
              <div class="info-content">
                <p class="info-label">Grade</p>
                <p class="info-value">{{ formatGradeLevel }}</p>
              </div>
            </div>
          </div>

          <div class="activity-details">
            <div class="activity-image-section">
              <img
                :src="activityImage"
                :alt="currentActivity.makercart_activity"
                class="activity-image"
              />
            </div>
            <div class="outcomes-section">
              <h3 class="outcomes-title">Lesson Outcomes</h3>
              <div
                class="formatted-outcomes"
                v-html="formattedLearningOutcomes"
                v-if="formattedLearningOutcomes"
              ></div>
              <div v-else>
                <p>No outcomes available.</p>
              </div>
            </div>
          </div>
          <div class="button-container">
            <ActionButton
              @click="handleBack"
              variant="outline"
              class="back-button"
              style="margin-bottom: 0px"
            >
              Back</ActionButton
            >
            <ActionButton
              @click="handleViewActivity"
              variant="primary"
              class="view-button"
              >View Activity
            </ActionButton>
          </div>
        </div>
        <div v-else class="error-container">
          <p>No activity available. Please try refreshing.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import ActionButton from "@/components/common/ActionButton.vue";
import BookIcon from "@/components/icons/BookIcon.vue";
import PeopleIcon from "@/components/icons/PeopleIcon.vue";
import RefreshIcon from "@/components/icons/RefreshIcon.vue";
import ViewIcon from "@/components/icons/ViewIcon.vue";
import { ref, computed, onMounted, defineEmits } from "vue";
import stemStudioService from "@/services/stemStudioService";
import { useMakerCart } from "@/components/maker-cart/catalog/useMakerCart";
import { useRouter } from "vue-router";

const emit = defineEmits(["back", "view-activity", "refresh"]);

const router = useRouter();

const { formatLearningOutcomes } = useMakerCart();
const activities = ref([]);
const currentActivity = ref(null);
const isLoading = ref(false);

const loadActivities = async () => {
  try {
    isLoading.value = true;
    const response = await stemStudioService.getAll({});

    if (response?.data?.data?.rows && response.data.data.rows.length > 0) {
      activities.value = response.data.data.rows;
      pickRandomActivity();
    } else {
      currentActivity.value = null;
    }
  } catch (error) {
    console.error("Error loading activities:", error);
    currentActivity.value = null;
  } finally {
    isLoading.value = false;
  }
};

const pickRandomActivity = () => {
  if (activities.value.length > 0) {
    const randomIndex = Math.floor(Math.random() * activities.value.length);
    currentActivity.value = activities.value[randomIndex];
  }
};

const formatGradeLevel = computed(() => {
  if (
    !currentActivity.value?.grade_level ||
    currentActivity.value.grade_level.length === 0
  ) {
    return "N/A";
  }
  return currentActivity.value.grade_level.join(", ");
});

const activityImage = computed(() => {
  if (
    !currentActivity.value?.image_thumbnail ||
    currentActivity.value.image_thumbnail.length === 0
  ) {
    return "/images/content-placeholder.svg";
  }
  return currentActivity.value.image_thumbnail[0];
});

const formattedLearningOutcomes = computed(() => {
  if (!currentActivity.value?.learning_outcomes) {
    return "";
  }
  return formatLearningOutcomes(currentActivity.value.learning_outcomes);
});

const handleBack = () => {
  emit("back");
};

const handleViewActivity = () => {
  router.push({
    name: "MakerCartPage",
    query: { item: currentActivity.value.makercart_id },
  });
};

const handleRefresh = () => {
  pickRandomActivity();
  emit("refresh");
};

onMounted(() => {
  loadActivities();
});
</script>

<style scoped>
.recommended-activity {
  background-color: #f2f3fb;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  min-height: 100vh;
  width: 100%;
}

.activity-card {
  background-color: white;
  border: 1px solid #fd72b3;
  border-radius: 16px;
  width: 100%;
  max-width: 864px;
  overflow: hidden;
}

.card-header {
  background-color: var(--pink-dark-1);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  padding: 32px;
  width: 100%;
}

.header-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: 45px;
  line-height: 52px;
  color: white;
  text-align: center;
  margin: 0;
}

.card-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: white;
  text-align: center;
  margin: 0;
  letter-spacing: 0.024px;
}

.card-content {
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.activity-header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

.title-badge-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.activity-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xl);
  line-height: 32px;
  color: #3b4648;
  margin: 0;
}

.subject-badge {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #dfe2e7;
  background-color: white;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #40444b;
  width: fit-content;
  letter-spacing: 0.035px;
}

.refresh-button {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 10px 16px;
  height: 40px;
  border: 1px solid #3b4648;
  border-radius: 8px;
  background-color: white;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #3b4648;
  transition: all 0.3s ease;
  letter-spacing: 0.014px;
}

.refresh-button:hover {
  background-color: #f2f3fb;
  border-color: #0478ce;
}

.activity-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 26px;
}

.activity-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
  margin: 0;
  letter-spacing: 0.024px;
}

.info-boxes {
  display: flex;
  gap: 8px;
  width: 100%;
}

.info-item-box {
  flex: 1;
  background-color: #f2f3fb;
  border-radius: 8px;
  padding: 0 0 0 12px;
  min-height: 60px;
  display: flex;
  gap: 12px;
  align-items: center;
}

.info-icon {
  width: 28.8px;
  height: 28.8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.info-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 36px;
}

.info-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #3b4648;
  margin: 0;
  letter-spacing: 0.048px;
}

.info-value {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #3b4648;
  margin: 0;
  letter-spacing: 0.035px;
}

.activity-details {
  display: flex;
  gap: 32px;
  width: 100%;
}

.activity-image-section {
  width: 300px;
  height: 300px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f5f5f5;
}

.activity-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.outcomes-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.outcomes-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xl);
  line-height: 32px;
  color: var(--primary-blue);
  margin: 0;
}

.outcomes-intro {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  margin: 0;
  letter-spacing: 0.024px;
}

.outcomes-list {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  margin: 0;
  padding-left: 24px;
  letter-spacing: 0.024px;
}

.outcomes-list li {
  margin-bottom: 0;
}

.formatted-outcomes {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  letter-spacing: 0.024px;
}

.formatted-outcomes :deep(ol) {
  margin: 0;
  padding-left: 24px;
}

.formatted-outcomes :deep(li) {
  margin-bottom: 0;
}

.formatted-outcomes :deep(p) {
  margin: 0;
  margin-bottom: 8px;
}

.formatted-outcomes :deep(p:last-child) {
  margin-bottom: 0;
}

.error-container {
  text-align: center;
  padding: 40px;
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  color: #6c737f;
}

.skeleton-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: 16px;
}

.skeleton-title-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.skeleton-title-large {
  height: 32px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  width: 85%;
}

.skeleton-badge {
  height: 28px;
  width: 100px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 24px;
}

.skeleton-refresh-button {
  height: 40px;
  width: 100px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
  flex-shrink: 0;
}

.skeleton-description {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-line {
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  width: 100%;
}

.skeleton-line.short {
  width: 70%;
}

.skeleton-info-box {
  background-color: #f2f3fb;
}

.skeleton-icon {
  width: 28.8px;
  height: 28.8px;
  background: linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-label {
  height: 12px;
  width: 60px;
  background: linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 4px;
}

.skeleton-value {
  height: 14px;
  width: 80px;
  background: linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-image-large {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-outcomes-title {
  height: 24px;
  width: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-outcomes-intro {
  height: 16px;
  width: 120px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 12px;
}

.skeleton-outcomes-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 24px;
}

.skeleton-outcome-item {
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  width: 100%;
}

.skeleton-outcome-item.short {
  width: 85%;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.button-container {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.back-button {
  background-color: white;
  border: 1px solid #bac1cc;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 56px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 12px;
  min-width: 126px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #6c737f;
  white-space: nowrap;
  letter-spacing: 0.024px;
  transition: all 0.3s ease;
}

.back-button:hover {
  background-color: #f2f3fb;
  border-color: #0478ce;
  color: #3b4648;
}

.view-button {
  background-color: #e00069;
  border: none;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 56px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: white;
  white-space: nowrap;
  letter-spacing: 0.024px;
  transition: all 0.3s ease;
}

.view-button:hover {
  background-color: #c0005a;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(224, 0, 105, 0.3);
}

@media (max-width: 768px) {
  .activity-details {
    flex-direction: column;
  }

  .activity-image-section {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
  }

  .card-content {
    padding: 0px;
  }

  .activity-header-section {
    flex-direction: column-reverse;
    align-items: flex-start;
    gap: 12px;
  }

  .info-boxes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .info-boxes > :last-child {
    grid-column: span 2;
  }

  .card-title {
    font-size: 32px;
    line-height: 40px;
  }

  .back-button,
  .view-button {
    width: 100%;
  }

  .refresh-button {
    align-self: flex-end;
  }

  .activity-title {
    font-size: var(--fs-big);
  }

  .formatted-outcomes {
    font-size: var(--fs-small);
  }
}
</style>
